import React, { useEffect, useState } from 'react';
import {
  MapContainer,
  Polygon,
  Marker,
  TileLayer,
  Tooltip,
  useMapEvents,
  useMap,
  Popup,
  FeatureGroup
} from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import L from 'leaflet';
import { useQuery } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  toggleSelectLandParcel,
  setZoom,
  addDrawnLandParcel,
  setLandParcels
} from 'reducers/createListingReducer';
import { calculatePolygonAreaHa, generatePolygonId } from 'helpers/gis';
import { GET_LAND_PARCELS } from 'graphql/gis';

const MapUpdateLocation = ({ location }) => {
  const map = useMap();
  useEffect(() => {
    map.setView([location.lat, location.lng], map.getZoom());
  }, [location, map]);

  return null;
};

MapUpdateLocation.propTypes = {
  location: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired
  }).isRequired
};

const Map = () => {
  const [topLeft, setTopLeft] = useState(null);
  const [bottomRight, setBottomRight] = useState(null);
  const { landParcels, selectedLandParcels, zoom, location } = useSelector(
    state => state.createListing
  );
  const { mapboxSatelliteStreetsV12URL } = useSelector(state => state.config);

  const dispatch = useDispatch();
  const { data } = useQuery(GET_LAND_PARCELS, {
    variables: {
      topLeftLat: topLeft?.lat,
      topLeftLng: topLeft?.lng,
      btmRightLat: bottomRight?.lat,
      btmRightLng: bottomRight?.lng
    },
    skip: !topLeft || !bottomRight || zoom < 16
  });

  useEffect(() => {
    if (!data) {
      return;
    }

    const landParcels = data.landParcels.items.map(p => ({
      id: p.id,
      coordinates: p.polygon.points.map(p => [p.lng, p.lat]),
      attributes: p.attributes,
      area: p.area
    }));
    dispatch(setLandParcels(landParcels));
  }, [data]);

  const setBounds = map => {
    const bounds = map.getBounds();
    setTopLeft(bounds.getNorthWest());
    setBottomRight(bounds.getSouthEast());
  };

  const MapEvents = () => {
    const map = useMapEvents({
      moveend: () => setBounds(map),
      zoomlevelschange: () => setZoom(map.getZoom())
    });
    return null;
  };

  const handlePolygonCreate = ({ layerType, layer }) => {
    if (layerType === 'polygon') {
      let coordinates = layer.getLatLngs()[0].map(p => [p.lng, p.lat]);
      coordinates = [...coordinates, coordinates[0]];
      dispatch(
        addDrawnLandParcel({
          id: generatePolygonId(coordinates),
          coordinates: coordinates,
          area: calculatePolygonAreaHa(coordinates),
          isDrawn: true
        })
      );
      layer.remove();
    }
  };

  return (
    <MapContainer
      zoom={17}
      zoomSnap={0.5}
      center={[location.lat, location.lng]}
      radius={200}
      className="h-100 mt-3"
      whenReady={e => {
        setBounds(e.target);
      }}
    >
      <FeatureGroup>
        <EditControl
          position="topright"
          edit={{
            edit: false,
            remove: false
          }}
          style={{ top: 100 }}
          onCreated={handlePolygonCreate}
          draw={{
            rectangle: false,
            polygon: true,
            circle: false,
            marker: false,
            circlemarker: false,
            polyline: false
          }}
          // onDrawCreate={handleOnDrawCreate(e)}
        ></EditControl>
        {/* component for drawing and editing polygon, rectangle */}
      </FeatureGroup>
      <TileLayer url={mapboxSatelliteStreetsV12URL} />
      {landParcels.map(parcel => {
        const defaultColor = selectedLandParcels.find(p => p.id === parcel.id)
          ? 'purple'
          : 'white';
        return (
          <Polygon
            key={parcel.id}
            pathOptions={{ color: defaultColor }}
            positions={parcel.coordinates.map(c => [c[1], c[0]])}
            eventHandlers={{
              click: () => dispatch(toggleSelectLandParcel(parcel)),
              mouseover: e => e.target.setStyle({ color: 'green' }),
              mouseout: e => e.target.setStyle({ color: defaultColor })
            }}
            weight={1}
          >
            <Tooltip direction="top">
              {parcel.attributes
                ?.filter(a => a.visible)
                .map(a => (
                  <p className="m-0 text-500" key={a.key}>
                    <b>{a.label}</b> {a.value}
                  </p>
                ))}
            </Tooltip>
          </Polygon>
        );
      })}
      <MapEvents />
      <MapUpdateLocation location={location} />
      <Marker
        key={location.formatted}
        position={[location.lat, location.lng]}
        icon={L.icon({
          iconUrl:
            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABkAAAApCAYAAADAk4LOAAAACXBIWXMAAAFgAAABYAEg2RPaAAADpElEQVRYCZ1XS1LbQBBtybIdiMEJKSpUqihgEW/xDdARyAnirOIl3MBH8NK7mBvkBpFv4Gy9IRSpFIQiRPyNfqkeZkY9HwmFt7Lm06+7p/vN2MmyDIrQ6QebALAHAD4AbFuWfQeAAACGs5H/w5jlsJJw4wMA+GhMFuMA99jIDJJOP+ihZwDQFmNuowWO1wS3viDXpdEdZPEc0odruj0EgN5s5H8tJOEEX8R3rbkMtcU34NTqhe5nSQTJ7Tkk80s6/Gk28scGiULguFBffgdufdEwWoQ0uoXo8hdAlooVH0REjISfwZSlyHGh0V5n6aHAtKTxXI5g6nQnMH0P4bEgwtR18Yw8Pj8QZ4ARUAI0Hl+fQZZGisGEBVwHr7XKzox57DXZ/ij8Cdwe2u057z9/wygOxRl4S2vSUHx1oucaMQGAHTrgtdag9mK5aN+Wx/uAAQ9Zenp/SRce4TpaNbQK4+sTcGqeTB/aIXv3XN5oj2VKqii++U0JunpZ8urxee4hvjqVc2hHpBDXuKKT9XMgVYJ1/1fPGSeaikzgmWWkMIi9bVf8UhotXxzORn5gWFchI8QyttlzjS0qpsaIGY2MMsujV/AUSdcY0dDpB6/EiOPYzclR1CI5mOez3ekHvrFLxa7cR5pTscfrXjk0Vhm5V2PqLUWnH3R5GbPGpMVD7E1ckXesKBQ7AS/vmQ1c0+kHuxpBj98lTCm8pbc5QRJRdZ6qHb/wGryXq3Lxszv+5gySuwvxueXySwYvHEjuQ9ofTGKYlrmK1EsCHMd5SoD7mZ1HHFCBHLNbMEshvrugqWLn01hpVVJhFgVGkDvK7hR6n2B+d9C7xsqWsbkqHv4cCsWezEb+o2SR+SFweUBxfA5wH7kShjKt2vWL57Px3GhIFEezkb8pxvUWHYhotAfCk2AtkEcxoOttrxUWDR5svb1emSQKj0WXK1HYIgFREbiBqmoZcB2RkbE+byMZiosorVgAZF1ID7yQhEs38wa7nUqNDezdlavC2HbBGSQkGgZ8uJVBmzeiKCRRpEa9ilWghORVeGB7BxeSKF5xqbFBkxBrFKUk/JHA7ppENQaCnCjthK+3opCEYyANztXmZN858cDYWSUSHk3A311GAZDvo6deNKUk1EsqnJoQlkYBNlmxQZeaMgmxoUokICoHDce351RCCiuKoirJWEgNOYvQplM2VCLhUqF7jf94rW9kHVUjQeheV4riv0i4ZOzzz/2y/+0KAOAfr4EE4HpCFhwAAAAASUVORK5CYII='
        })}
      >
        <Popup>{location.formatted}</Popup>
      </Marker>
    </MapContainer>
  );
};

export default Map;
